import { useCallback } from 'react'
import { Stack, Text } from '@leroy-merlin-br/backyard-react'
import { useRange } from 'react-instantsearch'
import { RangeBoundaries } from 'instantsearch.js/es/connectors/range/connectRange'

import { onFilterDataLayer } from 'site/AlgoliaEntryKey/utils/data-layer'
import { useAlgoliaContext } from 'site/AlgoliaEntryKey/context/algolia-context'

import { CustomPriceRange } from './components'
import { FilterTitle } from '../Filters/components'
import { Card } from '../Card'
import { filterToRender, MappedAttributes } from '../../utils/filterToRender'

export type RangeProps = {
  min: number
  max: number
}

export type UseRangeProps = {
  range: RangeProps
  refine: (rangeValue: RangeBoundaries) => void
  canRefine?: boolean
}

interface PriceRangeProps {
  attribute: MappedAttributes
  filterIndex: number
}

const PriceRange = ({ attribute, filterIndex }: PriceRangeProps) => {
  const {
    range,
    refine: setRange,
    canRefine: canRefineRange
  } = useRange({ attribute }) as UseRangeProps
  const { pageType, gaTrack, query } = useAlgoliaContext()

  const sendEvent = useCallback(
    (value: string | RangeBoundaries, index: number) => {
      const url = new URL(window.location.href)

      const filterElementValue =
        typeof value === 'string'
          ? JSON.parse(decodeURIComponent(value))
          : value

      onFilterDataLayer({
        gaTrack,
        term: query,
        pageType,
        filter: 'Normal',
        filterType: { index: filterIndex, value: filterToRender(attribute) },
        filterElement: { index, value: filterElementValue },
        isParamAlreadySet: url.searchParams.has(attribute)
      })
    },
    [attribute, filterIndex, gaTrack, pageType, query]
  )

  const handleSetRange = useCallback(
    (value: RangeBoundaries) => {
      sendEvent(value, 0)
      setRange(value)
    },
    [sendEvent, setRange]
  )

  if (!canRefineRange) {
    return null
  }

  return (
    <Card target="card">
      <Stack space="kilo">
        <Text as="div" size="giga" isBold noMargin>
          <FilterTitle
            filter={{
              name: filterToRender(attribute)
            }}
          />
        </Text>

        {canRefineRange && (
          <CustomPriceRange range={range} refine={handleSetRange} />
        )}
      </Stack>
    </Card>
  )
}

export default PriceRange
